import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Brain, Loader2, RefreshCw, CheckCircle, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

function StatCard({ label, value, sub, warn }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <p className="text-sm text-steel">{label}</p>
        <p className={`text-3xl font-bold mt-1 ${warn ? "text-orange" : "text-graphite"}`}>{value}</p>
        {sub && <p className="text-xs text-steel mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function AiMonitor() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["ai-monitor"],
    queryFn: () => api.get("/api/admin/ai-monitor").then((r) => r.data),
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-steel-light" />
      </div>
    );
  }

  const d = data || {};
  const transcribe = d.by_type?.transcribe || {};
  const rerank = d.by_type?.rerank || {};
  const budgetPct = d.daily_limit ? Math.round((d.total_calls_today / d.daily_limit) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-graphite">AI Monitor</h1>
          <p className="text-steel text-sm mt-1">
            Today's AI usage — transcription (OpenAI Whisper) and re-ranking (Anthropic Haiku).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={d.ai_enabled ? "default" : "secondary"} className="gap-1">
            {d.ai_enabled ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
            AI {d.ai_enabled ? "Enabled" : "Disabled"}
          </Badge>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 border border-border rounded-lg px-3 py-2 text-sm hover:bg-surface"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Budget bar */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-graphite">Daily Budget</p>
            <p className="text-sm text-steel">{d.total_calls_today} / {d.daily_limit} calls</p>
          </div>
          <div className="h-3 bg-surface rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${budgetPct > 80 ? "bg-red-500" : budgetPct > 50 ? "bg-orange" : "bg-green-500"}`}
              style={{ width: `${Math.min(budgetPct, 100)}%` }}
            />
          </div>
          <p className="text-xs text-steel mt-1">{d.remaining} calls remaining today (resets at midnight UTC)</p>
        </CardContent>
      </Card>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          label="Total calls today"
          value={d.total_calls_today ?? 0}
          sub={`Date: ${d.date}`}
        />
        <StatCard
          label="Fallback rate"
          value={`${Math.round((d.fallback_rate ?? 0) * 100)}%`}
          sub="AI skipped or failed"
          warn={(d.fallback_rate ?? 0) > 0.2}
        />
        <StatCard
          label="Transcriptions"
          value={transcribe.count ?? 0}
          sub={`avg ${transcribe.avg_latency_ms ?? 0} ms`}
        />
        <StatCard
          label="Re-rankings"
          value={rerank.count ?? 0}
          sub={`avg ${rerank.avg_latency_ms ?? 0} ms`}
        />
      </div>

      {/* Per-type breakdown */}
      {Object.keys(d.by_type || {}).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">By Call Type</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(d.by_type || {}).map(([type, stats]) => (
                <div key={type} className="flex items-center gap-4 py-2 border-b border-border last:border-0">
                  <Brain className="w-4 h-4 text-steel-light shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-graphite capitalize">{type}</p>
                    <p className="text-xs text-steel">
                      {stats.count} calls · avg {stats.avg_latency_ms} ms · {stats.fallbacks} fallbacks
                    </p>
                  </div>
                  <Badge variant={stats.fallbacks === 0 ? "outline" : "secondary"}>
                    {stats.fallbacks === 0 ? "All OK" : `${stats.fallbacks} fallbacks`}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-steel">
        Set <code className="bg-surface px-1 rounded">AI_ENABLED=false</code> to disable all AI calls globally.
        Set <code className="bg-surface px-1 rounded">AI_MAX_CALLS_PER_DAY</code> to adjust the daily budget limit.
      </p>
    </div>
  );
}
